import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import {
  AppHeader, AppLauncher, EnvironmentBadge, ErrorBoundary, Footer, GlyphErrorPage, GlyphLoader,
  IconSprite, LanguageToggle, Loader, McpStatus, StagingBanner, ThemeToggle, UserWidget, fleetEnvironment,
  launcherApps, type NavigationConfig,
} from "@weretradeit/ui-react";
import { useAuth } from "@weretradeit/hooks";
import { ladeGlyphSzeneImLeerlauf } from "./glyphScene";
import { captureAndPersistSsoToken, getCleanRedirectUrl } from "./utils/auth";

// Early SSO token capture for cross-domain redirects (?session= or ?token=)
captureAndPersistSsoToken();

// Lazy load page components for code splitting
const HomePage = lazy(() => import("./pages/HomePage"));
const CoveragePage = lazy(() => import("./pages/CoveragePage"));
const UnitTestsPage = lazy(() => import("./pages/UnitTestsPage"));
const ConventionsPage = lazy(() => import("./pages/ConventionsPage"));
const AutomationsPage = lazy(() => import("./pages/AutomationsPage"));

/**
 * Schluessel fuer Szene, Marke und Fehlerseiten.
 *
 * testingOctopus steht NICHT in design-system v3/fleet-apps.json (und nicht in
 * ui-react src/fleet.ts) — deshalb kein `FleetChrome` (dessen Name, Marke und
 * Art kaemen aus der Registry; ein unbekannter Schluessel warnt), sondern
 * `AppHeader` mit den Eigenschaften von 4.x.
 *
 * Fuer GlyphLoader/GlyphErrorPage/ErrorBoundary aber trotzdem der EIGENE
 * Schluessel, nicht "neuralWhale": alle drei lesen die Registry nicht
 * (ui-react 4.2.1 GlyphLoader.tsx/GlyphErrorPage.tsx: die Szene ist nur
 * `${appKey}:login`, die Texte kommen aus ui-strings), und das design-system
 * liefert `wt-glyphfield.testingOctopus.js` mit genau `testingOctopus:login`
 * (`testingOctopus` steht auch in GlyphScene, GlyphField.d.ts). Eine App, eine
 * Szene (Regel H11) — die Krake, nicht der Wal.
 */
const APP_KEY = "testingOctopus";

// Route type for hash-based routing
type Route = "/" | "/coverage" | "/unit-tests" | "/conventions" | "/automations";
const ROUTES: readonly Route[] = ["/", "/coverage", "/unit-tests", "/conventions", "/automations"];

/**
 * Der Hash als Route — oder `null` fuer eine unbekannte Adresse (404).
 * Leerer Hash und `#/` sind die Startseite, wie bisher. Ein `?…` im Hash
 * zaehlt nicht: public/assets/cache-buster.js laedt nach einem Deploy mit
 * `href + '?v=<Zeit>'` neu, und das landet hinter dem Hash.
 */
function routeFromHash(hash: string): Route | null {
  const h = hash.replace(/^#/, "").split("?")[0] || "/";
  return (ROUTES as readonly string[]).includes(h) ? (h as Route) : null;
}

/**
 * 4.x nimmt `navigation: { sections }` statt `navItems`. Jede Zeile traegt ein
 * Symbol aus dem Sprite — die Namen aus design-system v3/icons/nav-icons.json
 * fuer genau diese Beschriftungen (Regel H10/H10b), alle in names.json.
 * `href` mit Hash: „in neuem Tab" oeffnet dieselbe Seite.
 */
const NAVIGATION: NavigationConfig = {
  sections: [
    {
      items: [
        { label: "Home", href: "#/", icon: "home" },
        { label: "E2E Coverage", href: "#/coverage", icon: "gauge" },
        { label: "Unit Tests", href: "#/unit-tests", icon: "flask" },
        { label: "Conventions", href: "#/conventions", icon: "checklist" },
        { label: "Automations", href: "#/automations", icon: "zap" },
      ],
    },
  ],
};

/** Meldet einmal, dass die erste Seite steht (Effekt nach dem ersten Commit hinter Suspense). */
function ErsteSeiteSteht({ onSteht }: { onSteht: () => void }) {
  useEffect(() => { onSteht(); }, [onSteht]);
  return null;
}

export function App() {
  const [route, setRoute] = useState<Route | null>(() => routeFromHash(window.location.hash));
  const [ersteSeite, setErsteSeite] = useState(false);
  const markiereErsteSeite = useCallback(() => setErsteSeite(true), []);

  const auth = useAuth({
    redirectOnUnauthenticated: true,
    redirectUrl: getCleanRedirectUrl(),
    loginUrl: 'https://login.weretrade.com/api/auth/sso',
    authEndpoint: 'https://login.weretrade.com/api/auth/me',
  });
  const { user, profile, loading: authLoading, isAuthenticated } = auth;
  const status = authLoading ? 'loading' : isAuthenticated && user ? 'authenticated' : 'unauthenticated';

  // Hash-based routing
  useEffect(() => {
    const handleHashChange = () => setRoute(routeFromHash(window.location.hash));
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  /* Die GlyphField-Szene erst jetzt und erst im Leerlauf — nicht statisch aus
     main.tsx. Begruendung in glyphScene.ts. */
  useEffect(() => {
    ladeGlyphSzeneImLeerlauf();
  }, []);

  if (status === 'loading') {
    return <GlyphLoader appKey={APP_KEY} label="Session wird geprüft…" />;
  }

  if (status === 'unauthenticated' || !user) {
    return <GlyphLoader appKey={APP_KEY} label="Weiterleitung zu Login…" />;
  }

  // Render page based on route
  const renderPage = () => {
    switch (route) {
      case "/":
        return <HomePage />;
      case "/coverage":
        return <CoveragePage />;
      case "/unit-tests":
        return <UnitTestsPage />;
      case "/conventions":
        return <ConventionsPage />;
      case "/automations":
        return <AutomationsPage />;
      default:
        /* 404 der Flotte mit der eigenen Szene, INNERHALB der Huelle. Bis
           4.2.1 fiel jede unbekannte Adresse still auf die Startseite zurueck. */
        return <GlyphErrorPage appKey={APP_KEY} homeHref="#/" />;
    }
  };

  /* Erster Ladebildschirm (nichts sonst zu zeigen): GlyphLoader mit
     `testingOctopus:login`. Danach wartet jeder Seitenwechsel im Inhalt.
     Bis 4.2.1: `PageLoader`, ein 50vh-Kasten um `LoadingOverlay`. */
  const fallback = ersteSeite
    ? <Loader size="lg" label="Seite wird geladen" />
    : <GlyphLoader appKey={APP_KEY} label="TestingOctopus wird geladen…" />;

  /* Umgebung wie in FleetChrome aus dem Hostnamen (fleetEnvironment:
     staging./dev./localhost). Ohne `env` raten StagingBanner und
     EnvironmentBadge je eine eigene Regex — und nicht dieselbe. */
  const env = fleetEnvironment();

  return (
    <>
      <StagingBanner
        env={env}
        message={env === "dev" ? "Entwicklungsumgebung — Daten sind nicht produktiv." : undefined}
      />
      {/* Nur `app-shell`: `app-layout` war eine Klasse aus shared/ui/v2/base.css,
          die hier nie geladen wurde (Regel Z5). */}
      <div className="app-shell">
        {/* Ohne den Sprite rendert jedes <Icon> leer — Menuesymbole,
            Launcher-Knopf, Klapppfeile. scripts/copy-design-assets.mjs legt
            /icons/sprite.svg an. */}
        <IconSprite />

        {/* Kopfzeile in der Form von FleetChrome, von Hand (siehe APP_KEY):
            Launcher · Sprache in `actions`, Thema in `themeSlot`, Umgebung +
            Nutzer in `userSlot` — nur Eigenschaften aus der Positivliste der
            Flotten-Regel H3 (also kein orgName, kein brandingSlot; der leere
            Branding-Montagepunkt ist per `:empty` unsichtbar). Entfallen aus
            0.4.0: subtitle, logoUrl (→ mark), variant="glass", sticky,
            navItems/activeItem/onNavClick (→ navigation/activePath/onNavigate),
            showThemeToggle/showUserWidget (→ Slots; die Vanilla-Widgets aus
            index.html sind weg).

            hideInProduction: hier stand `hideInProduction={true}`. Die Vorgabe
            ist seit 3.0.0 `true` (0.4.0: `false`) — ohne die Eigenschaft
            bleibt es also bei „in Produktion nichts". */}
        <AppHeader
          appName="TestingOctopus"
          mark="testingOctopus"
          navigation={NAVIGATION}
          activePath={route ? `#${route}` : undefined}
          onNavigate={(item) => { if (item.href) window.location.hash = item.href; }}
          actions={
            <>
              {/* Die Launcher-Liste der Flotte aus der Registry. testingOctopus
                  selbst steht nicht darin, also ist keine Kachel „aktuell". */}
              <AppLauncher apps={launcherApps('testingOctopus', { env })} current="testingOctopus" />
              <LanguageToggle />
            </>
          }
          themeSlot={<ThemeToggle />}
          userSlot={
            <>
              <McpStatus endpoint="/api/mcp/health" pollMs={30000} />
              <EnvironmentBadge env={env} />
              {/* Wie bisher das Vanilla-Widget gegen login.weretrade.com;
                  Profil und Abmelden sind seit 4.0.0 eingebaut. */}
              <UserWidget
                authBaseUrl="/api/auth"
                user={{
                  displayName: profile?.display_name || user.username,
                  email: profile?.email || user.username,
                  role: profile?.role,
                  avatarUrl: profile?.avatar_url,
                }}
                showQuickApps={false}
              />
            </>
          }
        />

        <main className="app-main">
          {/* `appKey`, OHNE `fallback`: ein Renderfehler zeichnet
              `GlyphErrorPage kind="500"` in der Huelle; `key` setzt die Grenze
              beim Seitenwechsel zurueck. Die aeussere Grenze steht in main.tsx. */}
          <ErrorBoundary key={route ?? "404"} appKey={APP_KEY}>
            <Suspense fallback={fallback}>
              {renderPage()}
              {!ersteSeite && <ErsteSeiteSteht onSteht={markiereErsteSeite} />}
            </Suspense>
          </ErrorBoundary>
        </main>

        <Footer />
      </div>
    </>
  );
}
